"use client";

import { useTranslations } from "next-intl";
import { DelayAsterisk } from "../delay-asterisk";
import { SectionEyebrow, SectionTitle } from "../section-eyebrow";
import { ScrollReveal, ScrollStagger, StaggerItem } from "../scroll-reveal";

export function Process() {
  const t = useTranslations("process");
  const steps = t.raw("steps") as Array<{
    n: string;
    title: string;
    duration: string;
    description: string;
  }>;

  return (
    <section
      id="process"
      className="relative py-12 sm:py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal as="header" className="mb-10 flex flex-col gap-5 sm:max-w-2xl lg:mb-14">
          <div className="flex items-center justify-between">
            <SectionEyebrow>{t("eyebrow")}</SectionEyebrow>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 hidden sm:inline">
              / 7–14 jours
            </span>
          </div>
          <SectionTitle text={t("title")} italicLastWords={3} />
          <p data-blend className="text-base sm:text-lg">
            {t("subtitle")}
            <DelayAsterisk />
          </p>
        </ScrollReveal>

        <ScrollStagger
          as="ol"
          className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-border ring-1 ring-border md:grid-cols-2 lg:grid-cols-4"
          staggerChildren={0.1}
        >
          {steps.map((step, i) => (
            <StaggerItem
              key={step.n}
              as="li"
              className="group relative flex flex-col gap-4 bg-card p-5 transition-colors hover:bg-muted/40 sm:gap-5 sm:p-8"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  / {step.n}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {step.duration}
                </span>
              </div>
              <span
                aria-hidden
                className="font-display text-5xl leading-none text-foreground/80 transition-all duration-500 group-hover:text-foreground group-hover:-translate-y-0.5 sm:text-7xl lg:text-8xl"
              >
                {step.n}
              </span>
              <h3 className="font-sans text-xl font-medium tracking-tight text-foreground">
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {step.description}
              </p>
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className="absolute end-0 top-1/2 hidden h-px w-6 translate-x-full bg-aurora-cyan/30 lg:block"
                />
              )}
            </StaggerItem>
          ))}
        </ScrollStagger>
      </div>
    </section>
  );
}
